import type {
  ColorScheme,
  Effect,
  Fill,
  FontCollection,
  FontScheme,
  FormatScheme,
  Line,
  Theme,
} from '@slideforge/shared';
import type { Element } from '../xml.js';
import { parseColorElement, parseEffects, parseFillElement, parseLine, type FillContext } from './dml.js';

const SCHEME_SLOTS = [
  'dk1',
  'lt1',
  'dk2',
  'lt2',
  'accent1',
  'accent2',
  'accent3',
  'accent4',
  'accent5',
  'accent6',
  'hlink',
  'folHlink',
] as const;

export const FALLBACK_COLOR_SCHEME: ColorScheme = {
  dk1: '000000',
  lt1: 'FFFFFF',
  dk2: '44546A',
  lt2: 'E7E6E6',
  accent1: '4472C4',
  accent2: 'ED7D31',
  accent3: 'A5A5A5',
  accent4: 'FFC000',
  accent5: '5B9BD5',
  accent6: '70AD47',
  hlink: '0563C1',
  folHlink: '954F72',
};

export function parseTheme(themeRoot: Element, ctx: FillContext): Theme {
  const themeElements = themeRoot.child('a:themeElements');
  return {
    name: themeRoot.attr('name') ?? 'Theme',
    colorScheme: parseColorScheme(themeElements?.child('a:clrScheme')),
    fontScheme: parseFontScheme(themeElements?.child('a:fontScheme')),
    formatScheme: parseFormatScheme(themeElements?.child('a:fmtScheme'), ctx),
  };
}

/**
 * The theme's own colour scheme resolves `dk1`/`lt1` through `a:sysClr`, whose
 * `lastClr` attribute is the value PowerPoint actually painted. Falling back to
 * the system colour name alone gives black-on-black on a dark-themed OS.
 */
function parseColorScheme(clrScheme: Element | undefined): ColorScheme {
  if (!clrScheme) return FALLBACK_COLOR_SCHEME;
  const out: Record<string, string> = { ...FALLBACK_COLOR_SCHEME };
  for (const slot of SCHEME_SLOTS) {
    const el = clrScheme.child(`a:${slot}`);
    if (!el) continue;
    const srgb = el.child('a:srgbClr')?.attr('val');
    if (srgb) {
      out[slot] = srgb.toUpperCase();
      continue;
    }
    const sys = el.child('a:sysClr');
    const lastClr = sys?.attr('lastClr');
    if (lastClr) out[slot] = lastClr.toUpperCase();
  }
  return out as ColorScheme;
}

function parseFontScheme(fontScheme: Element | undefined): FontScheme {
  return {
    name: fontScheme?.attr('name') ?? 'Office',
    major: parseFontCollection(fontScheme?.child('a:majorFont'), 'Calibri Light'),
    minor: parseFontCollection(fontScheme?.child('a:minorFont'), 'Calibri'),
  };
}

function parseFontCollection(el: Element | undefined, fallback: string): FontCollection {
  const scripts: Record<string, string> = {};
  for (const font of el?.all('a:font') ?? []) {
    const script = font.attr('script');
    const typeface = font.attr('typeface');
    if (script && typeface) scripts[script] = typeface;
  }
  return {
    latin: el?.child('a:latin')?.attr('typeface') || fallback,
    eastAsian: el?.child('a:ea')?.attr('typeface') ?? '',
    complexScript: el?.child('a:cs')?.attr('typeface') ?? '',
    scripts,
  };
}

function parseFormatScheme(fmtScheme: Element | undefined, ctx: FillContext): FormatScheme {
  const fills: Fill[] = [];
  for (const el of fmtScheme?.child('a:fillStyleLst')?.children ?? []) {
    fills.push(parseFillElement(el, ctx));
  }

  const lines: Line[] = [];
  for (const el of fmtScheme?.child('a:lnStyleLst')?.all('a:ln') ?? []) {
    const line = parseLine(el, ctx);
    if (line) lines.push(line);
  }

  const effects: Effect[][] = [];
  for (const el of fmtScheme?.child('a:effectStyleLst')?.all('a:effectStyle') ?? []) {
    effects.push(parseEffects(el.child('a:effectLst')));
  }

  const backgroundFills: Fill[] = [];
  for (const el of fmtScheme?.child('a:bgFillStyleLst')?.children ?? []) {
    backgroundFills.push(parseFillElement(el, ctx));
  }

  return { fills, lines, effects, backgroundFills };
}

export { parseColorElement };
